import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Globe } from "lucide-react";
import { useLang, SECTIONS } from "../lib/i18n";

export const LOGO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_17d213b2-6013-4469-9901-85c024abd0d6/artifacts/9d66c6bdd260075b_B12C3107-0F66-4036-B086-DEE0D409F8DE%202.PNG";

const navCls = ({ isActive }) =>
  `whitespace-nowrap px-3 py-2.5 text-[13px] font-semibold tracking-wide transition-colors border-b-2 -mb-[2px] ${
    isActive
      ? "border-brk text-deepgreen"
      : "border-transparent text-ink hover:text-deepgreen hover:border-gold"
  }`;

export default function Masthead() {
  const { lang, setLang, t } = useLang();
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const today = new Date().toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header data-testid="masthead">
      <div className="bg-ink text-paper/90 text-[11px] font-mono tracking-wide">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-8">
          <span data-testid="masthead-date">{today}</span>
          <span className="hidden md:block text-gold/90">{t.tagline}</span>
          <div className="flex items-center gap-4">
            <button
              data-testid="lang-toggle"
              onClick={() => setLang(lang === "hi" ? "en" : "hi")}
              className="flex items-center gap-1.5 hover:text-gold transition-colors"
            >
              <Globe size={12} /> {lang === "hi" ? "English" : "हिंदी"}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-paper border-b-2 border-ink">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-6">
          <div className="hidden lg:block w-44 text-[11px] font-mono text-muted-foreground leading-relaxed">
            {t.edition}
            <br />
            <span className="text-deepgreen font-semibold">{lang === "hi" ? "जैसलमेर • जयपुर" : "Jaisalmer • Jaipur"}</span>
          </div>

          <Link to="/" data-testid="brand-home" className="group flex flex-col items-center">
            <img
              src={LOGO_URL}
              alt="The Vainor India"
              className="h-20 md:h-24 w-auto drop-shadow-sm transition-transform duration-300 group-hover:scale-[1.04]"
            />
            <span className="font-cinzel text-base md:text-lg font-extrabold tracking-[0.22em] text-deepgreen mt-1">
              THE VAINOR <span className="text-gold-accent">INDIA</span>
            </span>
          </Link>

          <div className="hidden lg:block w-44">
            <form onSubmit={submitSearch} data-testid="masthead-search-form">
              <div className="flex items-center border border-ink/30 bg-white focus-within:border-deepgreen transition-colors">
                <input
                  data-testid="masthead-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t.searchPlaceholder}
                  className="w-full bg-transparent text-sm px-3 py-1.5 outline-none placeholder:text-muted-foreground"
                />
                <button
                  data-testid="masthead-search-button"
                  type="submit"
                  className="px-2.5 text-ink hover:text-deepgreen transition-colors"
                  aria-label="search"
                >
                  <Search size={16} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <nav
        data-testid="section-nav"
        className="sticky top-0 z-40 bg-paper/95 backdrop-blur border-b-2 border-ink shadow-[0_1px_0_rgba(212,175,55,0.6)]"
      >
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-1 overflow-x-auto no-scrollbar">
          <NavLink to="/" end data-testid="nav-home" className={navCls}>
            {t.home}
          </NavLink>
          {SECTIONS.map((s) => (
            <NavLink key={s.id} to={`/section/${s.id}`} data-testid={`nav-${s.id}`} className={navCls}>
              {lang === "hi" ? s.hi : s.en}
            </NavLink>
          ))}
          <NavLink to="/districts" data-testid="nav-districts" className={navCls}>
            {lang === "hi" ? "ज़िले" : "Districts"}
          </NavLink>
          <NavLink to="/epaper" data-testid="nav-epaper" className={navCls}>
            {t.epaper}
          </NavLink>
          <Link to="/search" data-testid="nav-search" className="lg:hidden px-2.5 py-2.5 text-ink text-[13px] font-semibold flex items-center gap-1" aria-label={lang === "hi" ? "खोजें" : "Search"}>
            <Search size={16} /> {lang === "hi" ? "खोजें" : "Search"}
          </Link>
        </div>
      </nav>
    </header>
  );
}
